import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Divider
} from '@mui/material';
import { 
  Person, 
  AttachMoney, 
  Receipt, 
  HealthAndSafety, 
  People 
} from '@mui/icons-material';
import InputGroup, { 
  FormTextField, 
  FormCheckbox, 
  FormRadioGroup, 
  FormSelect,
  ConditionalField 
} from './InputGroup';
import FileUploader from './FileUploader';
import { 
  FORM_SECTIONS, 
  SECTION_TITLES, 
  TAX_OFFSETS, 
  HEALTH_COVER_CODES, 
  VEHICLE_METHODS, 
  WFH_RATES 
} from '../utils/constants';
import { 
  clientDetailsSchema, 
  incomeSchema, 
  deductionsSchema, 
  taxOffsetsSchema, 
  spouseInfoSchema 
} from '../utils/validationSchemas';

const FormSection = ({ 
  section, 
  formData, 
  onSectionComplete, 
  onBack, 
  isLastSection = false 
}) => {
  const getSectionIcon = (sectionId) => {
    switch (sectionId) {
      case FORM_SECTIONS.CLIENT_DETAILS: return <Person />;
      case FORM_SECTIONS.INCOME: return <AttachMoney />;
      case FORM_SECTIONS.DEDUCTIONS: return <Receipt />;
      case FORM_SECTIONS.TAX_OFFSETS: return <HealthAndSafety />;
      case FORM_SECTIONS.SPOUSE_INFO: return <People />;
      default: return <Person />;
    }
  };

  const getSectionSchema = (sectionId) => {
    switch (sectionId) {
      case FORM_SECTIONS.CLIENT_DETAILS: return clientDetailsSchema;
      case FORM_SECTIONS.INCOME: return incomeSchema;
      case FORM_SECTIONS.DEDUCTIONS: return deductionsSchema;
      case FORM_SECTIONS.TAX_OFFSETS: return taxOffsetsSchema;
      case FORM_SECTIONS.SPOUSE_INFO: return spouseInfoSchema;
      default: return clientDetailsSchema;
    }
  };

  const getInitialValues = (sectionId) => {
    const sectionData = formData[sectionId] || {};
    
    // Add default values for specific sections
    if (sectionId === FORM_SECTIONS.TAX_OFFSETS) {
      return {
        dependentChildren: 0,
        ...sectionData
      };
    }
    
    return sectionData;
  };

  const formik = useFormik({
    initialValues: getInitialValues(section),
    validationSchema: getSectionSchema(section),
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log('Form submitted with values:', values);
      onSectionComplete(section, values);
    }
  });

  // Reset form when section changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    formik.resetForm({ values: getInitialValues(section) });
  }, [section]);



  const renderClientDetails = () => (
    <>
      <InputGroup title="Personal Information">
        <FormTextField
          formik={formik}
          name="firstName"
          label="First Name"
          required
        />
        <FormTextField
          formik={formik}
          name="lastName"
          label="Last Name"
          required
        />
        <FormTextField
          formik={formik}
          name="email"
          label="Email Address"
          type="email"
          required
        />
        <FormTextField
          formik={formik}
          name="phone"
          label="Phone Number"
          required
        />
      </InputGroup>

      <InputGroup title="Address">
        <FormTextField
          formik={formik}
          name="address"
          label="Street Address"
          required
        />
        <FormTextField
          formik={formik}
          name="suburb"
          label="Suburb"
          required
        />
        <FormTextField
          formik={formik}
          name="state"
          label="State"
          required
        />
        <FormTextField
          formik={formik}
          name="postcode"
          label="Postcode"
          required
        />
      </InputGroup>

      <InputGroup title="Client Status">
        <FormCheckbox
          formik={formik}
          name="isNewClient"
          label="This is a new client"
        />
      </InputGroup>

      <ConditionalField condition={formik.values.isNewClient}>
        <InputGroup title="Bank Account Details">
          <FormTextField
            formik={formik}
            name="tfn"
            label="Tax File Number"
            required
          />
          <FormTextField
            formik={formik}
            name="bankName"
            label="Bank Name"
            required
          />
          <FormTextField
            formik={formik}
            name="bsb"
            label="BSB (6 digits)"
            required
            inputProps={{ maxLength: 6 }}
          />
          <FormTextField
            formik={formik}
            name="accountNumber"
            label="Account Number"
            required
          />
          <FormTextField
            formik={formik}
            name="accountName"
            label="Account Name"
            required
          />
        </InputGroup>
      </ConditionalField>
    </>
  );

  const renderIncome = () => (
    <>
      <InputGroup title="Income Sources - YES/NO Checklist">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          FOR ALL CLIENTS – If answer is YES to any of the below, please provide PAYG Payment Summaries and/or other documents as necessary.
        </Typography>
        
        <FormCheckbox
          formik={formik}
          name="hasSalaryWages"
          label="ITEM 1 - SALARY OR WAGES"
        />

        <FormCheckbox
          formik={formik}
          name="hasAllowancesTips"
          label="ITEM 2 - ALLOWANCES, TIPS, DIRECTORS FEES, EARNINGS, TIPS ETC"
        />

        <FormCheckbox
          formik={formik}
          name="hasEmployerLumpSum"
          label="ITEM 3 - EMPLOYER LUMP SUM PAYMENTS"
        />

        <FormCheckbox
          formik={formik}
          name="hasEmploymentTermination"
          label="ITEM 4 - EMPLOYMENT TERMINATION PAYMENTS"
        />

        <FormCheckbox
          formik={formik}
          name="hasAustGovtAllowances"
          label="ITEM 5 - AUSTRALIAN GOVT ALLOWANCES & PAYMENTS"
        />

        <FormCheckbox
          formik={formik}
          name="hasAustGovtPensions"
          label="ITEM 6 - AUST GOV PENSIONS AND ALLOWANCES"
        />

        <FormCheckbox
          formik={formik}
          name="hasAustAnnuitiesSuper"
          label="ITEM 7 - AUST ANNUITIES & SUPER INCOME STREAMS"
        />

        <FormCheckbox
          formik={formik}
          name="hasAustSuperLumpSum"
          label="ITEM 8 - AUSTRALIAN SUPER LUMP SUM PAYMENTS"
        />

        <FormCheckbox
          formik={formik}
          name="hasAttributedPersonalServices"
          label="ITEM 9 - ATTRIBUTED PERSONAL SERVICES INCOME"
        />

        <FormCheckbox
          formik={formik}
          name="hasInterest"
          label="ITEM 10 - INTEREST RECEIVED"
        />

        <FormCheckbox
          formik={formik}
          name="hasDividends"
          label="ITEM 11 - DIVIDENDS"
        />

        <FormCheckbox
          formik={formik}
          name="hasEmployeeShareScheme"
          label="ITEM 12 - EMPLOYEE SHARE SCHEME (ESS interest - Taxed Upfront Schemes)"
        />

        <FormCheckbox
          formik={formik}
          name="hasPartnershipsTrusts"
          label="ITEM 13 - PARTNERSHIPS/TRUSTS (MANAGED INVESTMENT FUNDS)"
        />

        <FormCheckbox
          formik={formik}
          name="hasCapitalGains"
          label="ITEM 18 - SALE OF ASSETS (CAPITAL GAINS TAX)"
        />

        <FormCheckbox
          formik={formik}
          name="hasForeignIncome"
          label="ITEM 20 - FOREIGN INCOME and/or FOREIGN TAX CREDITS"
        />

        <FormCheckbox
          formik={formik}
          name="hasRental"
          label="ITEM 21 - RENTAL INCOME AND EXPENSES"
        />

        <FormCheckbox
          formik={formik}
          name="hasOtherIncome"
          label="ITEM 24 - ANY OTHER INCOME"
        />
      </InputGroup>

      <InputGroup title="Supporting Documents">
        <FileUploader
          files={formik.values.documents || []}
          onFilesChange={(files) => formik.setFieldValue('documents', files)}
          accept="application/pdf,image/*"
          description="Upload dividend slips, interest statements, CGT contracts, etc."
        />
      </InputGroup>
    </>
  );

  const renderDeductions = () => (
    <>
      <InputGroup title="Vehicle Expenses">
        <FormCheckbox
          formik={formik}
          name="hasVehicle"
          label="Work-related vehicle expenses"
        />
        <ConditionalField condition={formik.values.hasVehicle}>
          <FormRadioGroup
            formik={formik}
            name="vehicleMethod"
            label="Vehicle Method"
            options={Object.entries(VEHICLE_METHODS).map(([key, value]) => ({
              value,
              label: value
            }))}
          />
          <ConditionalField condition={formik.values.vehicleMethod === 'Logbook Method (must be completed every 5 years)'}>
            <FormTextField
              formik={formik}
              name="logbookPercentage"
              label="Logbook Percentage (%)"
              type="number"
              required
              inputProps={{ min: 0, max: 100 }}
            />
          </ConditionalField>
          <ConditionalField condition={formik.values.vehicleMethod === 'Cents per Kilometre Method (85 cents per KM from 1 July 2024)'}>
            <FormTextField
              formik={formik}
              name="kilometres"
              label="Number of Business Kilometres Travelled"
              type="number"
              required
              inputProps={{ min: 0, step: 1 }}
              helperText="Rate: 85 cents per kilometre (from 1 July 2024)"
            />
          </ConditionalField>
        </ConditionalField>
      </InputGroup>

      <InputGroup title="Other Deductions">
        <FormCheckbox
          formik={formik}
          name="hasClothing"
          label="Work clothing"
        />
        <ConditionalField condition={formik.values.hasClothing}>
          <FormTextField
            formik={formik}
            name="clothingAmount"
            label="Clothing Amount ($)"
            type="number"
            required
          />
        </ConditionalField>

        <FormCheckbox
          formik={formik}
          name="hasEducation"
          label="Education & Training"
        />
        <ConditionalField condition={formik.values.hasEducation}>
          <FormTextField
            formik={formik}
            name="educationAmount"
            label="Education Amount ($)"
            type="number"
            required
          />
        </ConditionalField>

        <FormCheckbox
          formik={formik}
          name="hasMobile"
          label="Mobile & Internet"
        />
        <ConditionalField condition={formik.values.hasMobile}>
          <FormTextField
            formik={formik}
            name="mobileAmount"
            label="Mobile/Internet Amount ($)"
            type="number"
            required
          />
          <FormTextField
            formik={formik}
            name="mobilePercentage"
            label="Work Usage Percentage (%)"
            type="number"
            required
            inputProps={{ min: 0, max: 100 }}
          />
        </ConditionalField>

        <FormCheckbox
          formik={formik}
          name="hasHomeOffice"
          label="Home Office"
        />
        <ConditionalField condition={formik.values.hasHomeOffice}>
          <FormTextField
            formik={formik}
            name="homeOfficeHours"
            label="Hours worked from home"
            type="number"
            required
          />
          <FormSelect
            formik={formik}
            name="homeOfficeRate"
            label="Home Office Rate"
            options={Object.entries(WFH_RATES).map(([key, value]) => ({
              value: key,
              label: value
            }))}
            required
          />
          {formik.values.homeOfficeRate === '67c' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Using simplified method (67c/hour) - no additional records required
            </Alert>
          )}
        </ConditionalField>

        <FormCheckbox
          formik={formik}
          name="hasTools"
          label="Tools & Equipment"
        />
        <ConditionalField condition={formik.values.hasTools}>
          <FormTextField
            formik={formik}
            name="toolsAmount"
            label="Tools Amount ($)"
            type="number"
            required
          />
        </ConditionalField>

        <FormCheckbox
          formik={formik}
          name="hasProfessional"
          label="Professional Memberships"
        />
        <ConditionalField condition={formik.values.hasProfessional}>
          <FormTextField
            formik={formik}
            name="professionalAmount"
            label="Professional Memberships Amount ($)"
            type="number"
            required
          />
        </ConditionalField>

        <FormCheckbox
          formik={formik}
          name="hasOtherDeductions"
          label="Other Deductions"
        />
        <ConditionalField condition={formik.values.hasOtherDeductions}>
          <FormTextField
            formik={formik}
            name="otherDeductionsAmount"
            label="Other Deductions Amount ($)"
            type="number"
            required
          />
          <FormTextField
            formik={formik}
            name="otherDeductionsDescription"
            label="Description of Other Deductions"
            required
          />
        </ConditionalField>
      </InputGroup>
    </>
  );

  const renderTaxOffsets = () => (
    <>
      <InputGroup title="Tax Offsets">
        <FormRadioGroup
          formik={formik}
          name="taxOffset"
          label="Select applicable tax offset"
          options={Object.entries(TAX_OFFSETS).map(([key, value]) => ({
            value,
            label: value
          }))}
        />
      </InputGroup>

      <InputGroup title="Private Health Insurance">
        <FormCheckbox
          formik={formik}
          name="hasPrivateHealth"
          label="Private health insurance"
        />
        <ConditionalField condition={formik.values.hasPrivateHealth}>
          <FormSelect
            formik={formik}
            name="healthCoverCode"
            label="Health Cover Code"
            options={HEALTH_COVER_CODES}
            required
          />
        </ConditionalField>
      </InputGroup>

      <InputGroup title="Dependents">
        <FormTextField
          formik={formik}
          name="dependentChildren"
          label="Number of dependent children"
          type="number"
          inputProps={{ min: 0, step: 1 }}
          placeholder="0"
        />
      </InputGroup>

      <InputGroup title="Marital Status">
        <FormCheckbox
          formik={formik}
          name="isMarried"
          label="Married or de facto relationship"
        />
      </InputGroup>
    </>
  );

  const renderSpouseInfo = () => (
    <>
      <InputGroup title="Spouse Information">
        <FormTextField
          formik={formik}
          name="spouseFirstName"
          label="Spouse First Name"
        />
        <FormTextField
          formik={formik}
          name="spouseLastName"
          label="Spouse Last Name"
        />
      </InputGroup>

      <InputGroup title="Spouse Income Details">
        <FormTextField
          formik={formik}
          name="spouseIncome"
          label="Spouse Income ($)"
          type="number"
          inputProps={{ min: 0 }}
        />
        <FormTextField
          formik={formik}
          name="spouseFringeBenefits"
          label="Spouse Fringe Benefits ($)"
          type="number"
          inputProps={{ min: 0 }}
        />
        <FormTextField
          formik={formik}
          name="spouseRentalLosses"
          label="Spouse Rental Losses ($)"
          type="number"
        />
        <FormTextField
          formik={formik}
          name="spouseGovernmentPensions"
          label="Spouse Government Pensions ($)"
          type="number"
          inputProps={{ min: 0 }}
        />
      </InputGroup>
    </>
  );

  const renderSectionContent = () => {
    switch (section) {
      case FORM_SECTIONS.CLIENT_DETAILS:
        return renderClientDetails();
      case FORM_SECTIONS.INCOME:
        return renderIncome();
      case FORM_SECTIONS.DEDUCTIONS:
        return renderDeductions();
      case FORM_SECTIONS.TAX_OFFSETS:
        return renderTaxOffsets();
      case FORM_SECTIONS.SPOUSE_INFO:
        return renderSpouseInfo();
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        {getSectionIcon(section)}
        {SECTION_TITLES[section]}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <form key={section} onSubmit={formik.handleSubmit}>
          {renderSectionContent()}
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={onBack}
              disabled={!onBack}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
            >
              {isLastSection ? 'Complete & Generate PDF' : 'Next Section'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default FormSection; 